import {
  AfterContentInit,
  Component,
  ContentChild,
  Directive,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkTableModule } from '@angular/cdk/table';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { ChainDocument, Document } from '../../model';
import { PreviewDocument } from '../service/document.service';
import { DocumentReviewBadgeComponent } from './document-review-badge.component';

export interface DocumentTableContext {
  $implicit: Document;
  document: Document;
}

@Directive({
  selector: 'ng-template[documentTableActionButtons]',
  standalone: true,
})
export class DocumentTableActionButtonsDirective {
  constructor(public readonly template: TemplateRef<DocumentTableContext>) {}
}

@Directive({
  selector: 'ng-template[documentTableNameExtra]',
  standalone: true,
})
export class DocumentTableNameExtraDirective {
  constructor(public readonly template: TemplateRef<DocumentTableContext>) {}
}

@Directive({
  selector: 'ng-template[documentTableOtherCustomization]',
  standalone: true,
})
export class DocumentTableOtherCustomizationDirective {
  constructor(public readonly template: TemplateRef<unknown>) {}
}

@Component({
  selector: 'app-document-table',
  standalone: true,
  templateUrl: 'document-table.component.html',
  styleUrls: ['document-table.component.scss'],
  imports: [
    CommonModule,
    CdkTableModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    DocumentReviewBadgeComponent,
  ],
})
export class DocumentTableComponent implements OnChanges, AfterContentInit, DoCheck {
  @Input() documents: readonly Document[] | null = [];
  @Input() previewMap: Record<string, PreviewDocument> | null = null;
  @Input() showPreview = true;
  @Input() showPages = true;
  @Input() showTypeColumn = false;
  @Input() showDelete = false;
  @Input() disableRowClick = false;
  @Input() allowNonDownloadableClick = false;
  @Input() nameColumnLabel = 'Name_tr';
  @Input() typeColumnLabel = 'Type_tr';

  @Output() deleteDocument = new EventEmitter<Document>();
  @Output() rowClick = new EventEmitter<Document>();

  @ContentChild(DocumentTableActionButtonsDirective) actionButtons?: DocumentTableActionButtonsDirective;
  @ContentChild(DocumentTableNameExtraDirective) nameExtra?: DocumentTableNameExtraDirective;
  @ContentChild(DocumentTableOtherCustomizationDirective) otherCustomization?: DocumentTableOtherCustomizationDirective;

  @ViewChild(MatTable) private table?: MatTable<Document>;
  private readonly iterableDiffers = inject(IterableDiffers);
  private documentsDiffer?: IterableDiffer<Document>;
  displayedColumns: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if ('documents' in changes) {
      this.setupDiffer();
    }
    this.updateColumns();
  }

  ngDoCheck(): void {
    this.renderRowsIfNeeded();
  }

  ngAfterContentInit(): void {
    this.updateColumns();
  }

  getPreview(document: Document): string | undefined {
    const entry = this.previewMap?.[document.id!];
    const preview = entry?.preview;
    if (!preview) {
      return undefined;
    }
    const contentType = (document as any).contentType || (entry as any)?.contentType || 'image/png';
    return `data:${contentType};base64,${preview}`;
  }

  hasPreviewError(document: Document): boolean {
    return !!(document as PreviewDocument).error_type || !!this.previewMap?.[document.id!]?.error_type;
  }

  onDelete(document: Document, event: Event) {
    event.stopPropagation();
    this.deleteDocument.emit(document);
  }

  onRowClick(document: Document) {
    if (this.disableRowClick) {
      return;
    }
    this.rowClick.emit(document);
  }

  isPixelated(document: Document): boolean {
    return (document as ChainDocument).preview === 'pixelated';
  }

  isDownloadable(document: Document): boolean {
    const chainDoc = document as ChainDocument;
    return chainDoc.downloadable !== false;
  }

  canClickRow(document: Document): boolean {
    return this.allowNonDownloadableClick || this.isDownloadable(document);
  }

  getDisplayTitle(document: Document): string {
    return document.title || document.type?.name || '';
  }

  trackById = (_: number, document: Document) => document.id ?? document.title ?? _;

  private updateColumns() {
    const columns: string[] = [];
    if (this.showPreview) {
      columns.push('preview');
    }
    if (this.showTypeColumn) {
      columns.push('type');
    }
    columns.push('name');
    if (this.showPages) {
      columns.push('pages');
    }
    if (this.showDelete || this.actionButtons) {
      columns.push('actions');
    }
    this.displayedColumns = columns;
  }

  private setupDiffer() {
    const data = this.documents ?? [];
    if (!this.documentsDiffer && data) {
      this.documentsDiffer = this.iterableDiffers.find(data).create<Document>();
    }
    this.documentsDiffer?.diff(data);
  }

  private renderRowsIfNeeded() {
    if (!this.table || !this.documentsDiffer || !this.documents) {
      return;
    }
    if (this.documentsDiffer.diff(this.documents)) {
      this.table.renderRows();
    }
  }
}
